import * as React from "react";
import { withStores } from "stores";
import { observer } from "mobx-react";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { Viewport, ViewSmall, ViewMedium } from "bernie-view";
import { MerchCampaignProps } from "./typings";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { IsomorphicCarousel } from "components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { useLocalization } from "@shared-ui/localization-context";
import { CampaignRecommendationsWithOffersQuery } from "../../../../__generated__/api";
import {
  CarouselModuleViewTypes,
  MerchCampaignCard,
} from "../../shared/Merchandising/Common/MerchCampaignCard/MerchCampaignCard";
import { UitkLayoutGrid } from "uitk-react-layout-grid";
import { UitkLoader } from "uitk-react-loader";
import { UitkSecondaryButton } from "uitk-react-button";
import { UitkHeading, UitkHeadingProps } from "uitk-react-text";
import Campaigns = CampaignRecommendationsWithOffersQuery.Campaigns;
import { CampaignHeader } from "../../shared/Merchandising/Common/CampaignHeader/CampaignHeader";
import { locKeys } from "./l10n";
import { CampaignPlaceholder } from "../../shared/Merchandising/Common/CampaignPlaceholder/CampaignPlaceholder";
import {
  Action,
  FlexTrackingInfo,
  sendDelayedClickstreamTrackEvent,
} from "components/utility/analytics/FlexAnalyticsUtils";
import { useClickTracker } from "@shared-ui/clickstream-analytics-context";

const MerchCampaign = withStores(
  "routeStore",
  "merchCampaigns",
  "flexViewModel"
)(
  observer((props: MerchCampaignProps) => {
    const { flexComponent, merchCampaigns, context, flexViewModel, routeStore } = props;
    const { model } = flexComponent;
    const keyHelper = new ItemKeyHelper(flexComponent.id);
    const { lat, long, origin, referrer } = props.routeStore.queryParams;
    const userLatitude = lat || flexViewModel.flexContext.userContext.userLocation?.latitude;
    const userLongitude = long || flexViewModel.flexContext.userContext.userLocation?.longitude;
    const userTripState = props.routeStore.queryParams.userTripState;
    const pageName = flexViewModel.trackingContext?.pageName;
    const showMoreButtonRef: React.RefObject<HTMLButtonElement> = React.createRef();

    if (!model) {
      return null;
    }

    const {
      numberOfCampaigns,
      destinationView,
      hotelInventoryView,
      displayDate,
      displayDescription,
      displayPrice,
      paginationEnabled,
      filterByProductType,
      paginationMaxCampaigns,
      title,
      filterByRecommendationType,
      enableLivePricing,
    } = model;

    React.useEffect(() => {
      if (merchCampaigns?.campaigns?.length === 0) {
        fetchCampaigns();
      }
    }, []);

    const fetchCampaigns = async () => {
      showMoreButtonRef.current?.blur();
      await merchCampaigns.fetchRecommendedCampaigns(
        context,
        numberOfCampaigns,
        paginationEnabled ? paginationMaxCampaigns : null,
        filterByProductType,
        filterByRecommendationType,
        userLatitude,
        userLongitude,
        origin,
        pageName,
        userTripState,
        referrer,
        enableLivePricing
      );
    };

    const openDialog = (dialogPath: string) => {
      routeStore.updateSearch({ pwaDialog: dialogPath });
    };

    const closeDialog = () => {
      routeStore.closeDialog("pwaDialog");
    };

    const renderOffers = (campaign: Campaigns) =>
      campaign.offers?.length && campaign.offers?.length >= 4 && campaign.productTypes.length > 0;

    const { formatText } = useLocalization();
    const titleConfig: UitkHeadingProps = { level: 1, size: 800 };
    const track = useClickTracker();

    const defaultUndefinedVisibleItems = CarouselModuleViewTypes.DEFAULT;

    const VisibleItemsMap = {};
    VisibleItemsMap[CarouselModuleViewTypes.VR] = { lg: 2, md: 2, sm: 2 };
    VisibleItemsMap[CarouselModuleViewTypes.REDESIGN] = { lg: 4, md: 4, sm: 2 };
    VisibleItemsMap[CarouselModuleViewTypes.DEFAULT] = { lg: 3, md: 2, sm: 1 };

    const getVisibleItemsForModelView = (modelView: string) => {
      return VisibleItemsMap[modelView ?? defaultUndefinedVisibleItems];
    };

    const numberOfVisibleItems = getVisibleItemsForModelView(model.view);

    const renderCarousel = (campaign: Campaigns, visibleItem: any) => {
      return (
        <IsomorphicCarousel
          className="carousel-component all-t-margin-three"
          itemsVisible={visibleItem}
          buttonText={{
            nextButton: formatText("carousel.item.next"),
            prevButton: formatText("carousel.item.prev"),
          }}
          pageBy={1}
          peekWidth={0.75}
        >
          {campaign.offers?.map((offer, cardIndex) => {
            return (
              <div className="uitk-height-item uitk-cell" key={keyHelper.next()}>
                <MerchCampaignCard
                  offer={offer}
                  cardIndex={cardIndex}
                  campaignId={campaign.id}
                  productTypes={campaign.productTypes}
                  recommendedType={campaign.recommendedType}
                  openDialog={openDialog}
                  closeDialog={closeDialog}
                  destinationView={destinationView}
                  hotelInventoryView={hotelInventoryView}
                  displayDate={displayDate}
                  displayDescription={displayDescription}
                  displayPrice={displayPrice}
                  border={true}
                  moduleName="MerchCampaign"
                />
              </div>
            );
          }) || []}
        </IsomorphicCarousel>
      );
    };

    const callback = (id: string) => {
      const flexTracking: FlexTrackingInfo = {
        moduleName: id,
        rfrr: `${flexViewModel.trackingContext?.pageName}.pagination`,
        action: Action.CLICK,
      };
      sendDelayedClickstreamTrackEvent(flexTracking, track);
    };

    return merchCampaigns.campaigns.length === 0 ? (
      !merchCampaigns.isLoading ? null : (
        <CampaignPlaceholder numberOfCampaigns={numberOfCampaigns} formatText={formatText} />
      )
    ) : (
      <div
        data-testid="merch-campaign"
        className="MerchCampaign uitk-cell all-b-margin-three"
        id={flexComponent.id}
        data-fm={flexComponent.fmId}
      >
        {title && (
          <div className="uitk-type-center all-t-padding-four all-t-margin-four">
            <UitkHeading {...titleConfig}>{title}</UitkHeading>
          </div>
        )}

        {merchCampaigns.campaigns?.filter(renderOffers).map((campaign: Campaigns, campaignIndex: number) => {
          return (
            <div className="all-y-padding-six" data-testid="merch-campaign-carousel" key={keyHelper.next()}>
              <CampaignHeader campaign={campaign} openDialog={openDialog} closeDialog={closeDialog} />

              <Viewport>
                <ViewSmall>{renderCarousel(campaign, numberOfVisibleItems)}</ViewSmall>
                {model.view === CarouselModuleViewTypes.VR ? (
                  <ViewMedium>
                    <UitkLayoutGrid columns={4} space="three" className="all-x-padding-twelve">
                      {campaign.offers?.slice(0, 4).map((offer, cardIndex) => {
                        return (
                          <div className="uitk-height-item" key={keyHelper.next()} data-testid="merch-grid">
                            <MerchCampaignCard
                              offer={offer}
                              cardIndex={cardIndex}
                              campaignId={campaign.id}
                              productTypes={campaign.productTypes}
                              recommendedType={campaign.recommendedType}
                              openDialog={openDialog}
                              closeDialog={closeDialog}
                              destinationView={destinationView}
                              hotelInventoryView={hotelInventoryView}
                              displayDate={displayDate}
                              displayDescription={displayDescription}
                              displayPrice={displayPrice}
                              border={true}
                              moduleName="MerchCampaign"
                            />
                          </div>
                        );
                      }) || []}
                    </UitkLayoutGrid>
                  </ViewMedium>
                ) : (
                  <ViewMedium>{renderCarousel(campaign, numberOfVisibleItems)}</ViewMedium>
                )}
              </Viewport>
            </div>
          );
        })}

        {paginationEnabled &&
          merchCampaigns.hasNextPage &&
          !(merchCampaigns.campaigns.length === paginationMaxCampaigns) && (
            <div className="all-b-padding-six uitk-type-center">
              <UitkSecondaryButton
                name="showMoreButton"
                type="button"
                onClick={fetchCampaigns}
                text={formatText(locKeys.showMore)}
                domRef={showMoreButtonRef}
                analytics={{
                  id: "merch.campaign",
                  callback,
                }}
              />
            </div>
          )}
        {merchCampaigns.isLoading && (
          <UitkLayoutFlex justifyContent="center">
            <UitkLayoutFlexItem className="all-t-margin-four">
              <div>
                <UitkLoader />
              </div>
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        )}
      </div>
    );
  })
);
export default MerchCampaign;
